import React, { forwardRef, Ref, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';

import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import EmbedModalWithGreenHeader from 'src/presentation/components/pages/shared/EmbedModalWithGreenHeader';
import StyledFooter from 'src/presentation/components/forms/Footer';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { getGladOsOrigin } from 'src/presentation/components/pages/helpers';
import { ActionAdapter, GuideInfo } from 'src/presentation/components/pages/glados/actionAdapters/ActionAdapters';

export const ACTIVE = 'Set as active' as const;
export const INACTIVE = 'Set as inactive' as const;

type ShowModalProps = {
  params?: unknown;
  subscribe: (params: unknown) => void;
};

export type ModalWrapperRef = {
  show: ({ params, subscribe }: ShowModalProps) => void;
  close: () => void;
};

const ActivateInactivateModal = forwardRef((_, ref: Ref<ModalWrapperRef>) => {
  const [modalVisible, setModalVisible] = useState(false);
  const refCallbacks = useRef<ShowModalProps | null>(null);

  useImperativeHandle(ref, () => ({
    show(props) {
      refCallbacks.current = props;
      setModalVisible(true);
    },
    close: () => setModalVisible(false),
  }));

  const handleClose = (data: unknown) => {
    if (refCallbacks.current?.subscribe && data) {
      refCallbacks.current.subscribe(data);
    }
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    refCallbacks.current = null;
  };

  if (!modalVisible) return null;

  let guides: GuideInfo[] | undefined = [];
  let actionAdapter: ActionAdapter | null = null;

  if (
    refCallbacks.current?.params &&
    typeof refCallbacks.current?.params === 'object' &&
    'actionAdapter' in refCallbacks.current?.params &&
    'guides' in refCallbacks.current?.params
  ) {
    guides = refCallbacks.current?.params.guides as GuideInfo[];
    actionAdapter = refCallbacks.current?.params.actionAdapter as ActionAdapter;
  }

  return (
    <StyledGreenHeaderModal
      content={<ActivateInactivateModalForm onClose={handleClose} actionAdapter={actionAdapter} guides={guides} />}
      disableOutsideClick={true}
      modalSize={ModalSize.MEDIUM}
      onCancel={handleCloseModal}
      title={actionAdapter ? (actionAdapter.active ? INACTIVE : ACTIVE) : ''}
    />
  );
});

const ActivateInactivateModalForm = ({ onClose, actionAdapter, guides }) => {
  const handleCancel = () => onClose();

  const handleSave = () => {
    onClose({ actionAdapter });
  };

  const modalContent = actionAdapter.active ? (
    <InactivateModalContent guides={guides} actionAdapter={actionAdapter} />
  ) : (
    <ActivateModalContent actionAdapter={actionAdapter} />
  );

  return (
    <>
      {modalContent}

      <RoundedFooter>
        {guides.length ? (
          <Button data-aid="modal-close" onClick={handleCancel} type={ButtonTypes.PRIMARY}>
            Close
          </Button>
        ) : (
          <>
            <Button data-aid="modal-close" onClick={handleCancel} type={ButtonTypes.SECONDARY}>
              Cancel
            </Button>

            <Button data-aid="modal-save" onClick={handleSave} type={ButtonTypes.PRIMARY}>
              {actionAdapter.active ? INACTIVE : ACTIVE}
            </Button>
          </>
        )}
      </RoundedFooter>
    </>
  );
};

const GuidesLinkList = ({ guides }) => {
  const gladosOrigin = getGladOsOrigin(window.location.origin);
  return (
    <LinkSection>
      {guides.map(guide => {
        return guide.cyberThreads.map(thread => {
          return (
            <a
              href={`${gladosOrigin}/sidekick/guides/${guide.id}/txguides/${thread.id}`}
              key={guide.id}
              target="_blank"
              rel="noreferrer"
            >
              {thread.name}
            </a>
          );
        });
      })}
    </LinkSection>
  );
};

const ActivateModalContent = ({ actionAdapter }) => {
  return (
    <ModalDescription>
      You&apos;re about to set <strong>{actionAdapter.name}</strong> as active. Any recent changes will be saved and
      will be available in Guides and the App Platform.
    </ModalDescription>
  );
};

const InactivateModalContent = ({ guides, actionAdapter }) => {
  if (!actionAdapter) {
    return null;
  }

  return (
    <>
      {guides.length ? (
        <>
          <ModalDescription>
            Before setting <strong>{actionAdapter.name}</strong> as inactive, Please deselect from the sections within
            these Guides:
          </ModalDescription>
          <GuidesLinkList guides={guides} />
        </>
      ) : (
        <ModalDescription>
          You&apos;re about to set <strong>{actionAdapter.name}</strong> as inactive. Please ensure no Guides are
          currently using this App Actions, as it will no longer be available as an option.
        </ModalDescription>
      )}
    </>
  );
};

const StyledGreenHeaderModal = styled(EmbedModalWithGreenHeader)`
  padding: 0;
`;

const RoundedFooter = styled(StyledFooter)`
  border-radius: 0 0 ${p => p.theme.borderRadius.large} ${p => p.theme.borderRadius.large};
  border-top: 1px solid ${p => p.theme.colors.gray100};
  gap: ${p => p.theme.spacing.medium};
`;

const LinkSection = styled.div`
  display: flex;
  min-width: fit-content;
  flex-direction: column;
  align-items: start;
  padding: 0 ${p => p.theme.spacing.insetLarge} ${p => p.theme.spacing.insetLarge} ${p => p.theme.spacing.insetLarge};
  line-height: ${p => p.theme.lineHeight.base};
`;

export const ModalDescription = styled.div`
  color: ${p => p.theme.colors.black};
  align-self: stretch;

  font-family: var(--font-inter);
  font-style: normal;
  font-weight: ${p => p.theme.fontWeight.normal};
  line-height: ${p => p.theme.lineHeight.base};

  padding: ${p => p.theme.spacing.insetLarge};
  text-align: justify;
`;

export default ActivateInactivateModal;
